import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Spinner,
  Alert,
  InputGroup,
  Form,
  Table,
} from "react-bootstrap";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Base_URL } from "../url";
import "../Styling/AllProductionOrders.css";

export default function AllProductionOrders() {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const FETCH_ENDPOINT = `${Base_URL}/ProductionExecution`;

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(FETCH_ENDPOINT);

      const responseData = Array.isArray(response?.data)
        ? response.data
        : [];

      setOrders(responseData);
    } catch (err) {
      console.error("Error fetching production orders:", err);

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch production orders"
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (time) => {
    if (!time) return "-";

    return time?.slice(0, 5);
  };

  const filteredOrders = useMemo(() => {
    if (!query.trim()) return orders;

    const searchValue = query.toLowerCase();

    return orders.filter((item) => {
      return (
        String(item?.productionOrderId ?? "")
          .toLowerCase()
          .includes(searchValue) ||
        String(item?.productionPlanId ?? "")
          .toLowerCase()
          .includes(searchValue) ||
        String(item?.productionOrderStatusId ?? "")
          .toLowerCase()
          .includes(searchValue)
      );
    });
  }, [orders, query]);

  return (
    <div className="apo-page">
      {/* ======================================================
          HEADER
      ====================================================== */}

      <div className="apo-header">
        <div className="apo-title-area">
          <h2>Production Orders</h2>

          <p className="apo-subtitle">
            Manage and execute production orders
          </p>
        </div>

        <Button
          className="apo-refresh-btn"
          onClick={fetchOrders}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" />
              Refreshing...
            </>
          ) : (
            "Refresh Orders"
          )}
        </Button>
      </div>

      {/* ======================================================
          ERROR
      ====================================================== */}

      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setError("")}
          className="apo-alert"
        >
          {error}
        </Alert>
      )}

      {/* ======================================================
          SEARCH
      ====================================================== */}

      <div className="apo-search-card">
        <InputGroup>
          <Form.Control
            placeholder="Search by Production Order ID, Plan ID or Status..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="apo-search-input"
          />

          <Button
            variant="outline-secondary"
            className="apo-clear-btn"
            onClick={() => setQuery("")}
          >
            Clear
          </Button>
        </InputGroup>
      </div>

      {/* ======================================================
          TABLE CARD
      ====================================================== */}

      <div className="apo-table-card">
        {loading && orders.length === 0 ? (
          <div className="apo-loading">
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="apo-table-wrapper">
            <Table responsive hover className="apo-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Plan ID</th>
                  <th>Planned Start</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Approx Cost</th>
                  <th>Organisation</th>
                  <th>Created On</th>
                  <th>Execute</th>
                  <th>Goods Issue</th>
                </tr>
              </thead>

              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="apo-empty">
                      No Production Orders Found
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((item, index) => (
                    <tr
                      key={`${item?.productionOrderId ?? "order"}-${index}`}
                    >
                      <td>
                        <span className="apo-id-chip">
                          {item?.productionOrderId ?? "-"}
                        </span>
                      </td>

                      <td>{item?.productionPlanId ?? "-"}</td>

                      <td>
                        <div className="apo-date-cell">
                          <span>
                            {formatDate(item?.plannedStartDate)}
                          </span>

                          <small>
                            {formatTime(item?.plannedStartTime)}

                            {item?.plannedStartTimezone
                              ? ` (${item?.plannedStartTimezone})`
                              : ""}
                          </small>
                        </div>
                      </td>

                      <td>
                        <span className="apo-duration-chip">
                          {item?.approxDuration ?? "-"}
                          {item?.approxDuration ? " H" : ""}
                        </span>
                      </td>

                      <td>
                        <span className="apo-status-badge">
                          {item?.productionOrderStatusId ?? "-"}
                        </span>
                      </td>

                      <td>
                        <span className="apo-cost-chip">
                          {item?.approxCost
                            ? `${item?.approxCost} ${
                                item?.approxCostCurrency ?? ""
                              }`
                            : "-"}
                        </span>
                      </td>

                      <td>{item?.organisationId ?? "-"}</td>

                      <td>
                        <div className="apo-date-cell">
                          <span>
                            {formatDate(item?.createddate)}
                          </span>

                          <small>
                            {formatTime(item?.createdtime)}
                          </small>
                        </div>
                      </td>

                      <td>
                        <Button
                          className="apo-execute-btn"
                          disabled={!item?.productionOrderId}
                          onClick={() =>
                            navigate(
                              `/executeProduction-order/${item?.productionOrderId}`
                            )
                          }
                        >
                          Execute
                        </Button>
                      </td>

                      <td>
                        <Button
                          className="apo-report-btn"
                          disabled={!item?.productionOrderId}
                          onClick={() =>
                            navigate(
                              `/goodsIssueReport/${item?.productionOrderId}`
                            )
                          }
                        >
                          View Report
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}